import { useState, useEffect, useMemo } from 'react';
import { fetchBuffer } from '../audio/gameAudio';

/**
 * Preloads URL-based game audio and reports when all are ready.
 *
 * Uses the shared Web Audio fetch+decode path so:
 *   - iOS Safari actually preloads (HTMLAudioElement#preload="auto" is silently
 *     ignored without a user gesture, which left iOS players staring at a 10s
 *     loading screen even when sounds were tiny).
 *   - The decoded AudioBuffer is cached and reused by createAudioPool, so the
 *     work isn't duplicated when the game starts.
 *
 * Synth sounds (synth:...) are skipped and don't block loading.
 */
const useAudioPreload = (settings, soundKeys = []) => {
  const soundKeysStr = Array.isArray(soundKeys) ? soundKeys.join(',') : '';
  const urls = useMemo(() => {
    if (!settings || !soundKeysStr) return [];
    const keys = soundKeysStr.split(',');
    const isValid = (s) =>
      typeof s === 'string' &&
      s.length > 4 &&
      !s.startsWith('synth:') &&
      (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('/'));
    return keys
      .map((k) => settings[k])
      .filter(Boolean)
      .filter(isValid);
  }, [settings, soundKeysStr]);

  const urlsKey = urls.join('|');
  const [audioLoaded, setAudioLoaded] = useState(() => urls.length === 0);

  useEffect(() => {
    if (urls.length === 0) {
      setAudioLoaded(true);
      return;
    }

    setAudioLoaded(false);
    let cancelled = false;

    // Cap the wait so a stuck/slow asset never blocks the game forever.
    const TIMEOUT_MS = 5000;
    let timeoutId = setTimeout(() => {
      if (!cancelled) setAudioLoaded(true);
    }, TIMEOUT_MS);

    Promise.all(urls.map((src) => fetchBuffer(src).catch(() => null))).finally(() => {
      if (cancelled) return;
      clearTimeout(timeoutId);
      setAudioLoaded(true);
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [urlsKey]);

  return { audioLoaded };
};

export default useAudioPreload;
